import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { pendingCueFor, rememberCue, clearCue } from '@/lib/spotlightCue';
import { isOpenerCue, setupIntentSearch } from '@/lib/setupIntents';

/**
 * Setup handoff controller. A link with `?cue=<id>` (Desk Setup, Your Next Move, Ask Cannafax)
 * lands on the page with the task already in front of the person:
 *  - opener cues (see `setupIntents.ts`) open their dialog or menu once, as if clicked;
 *  - every other cue scrolls its control into view and focuses it.
 * No glowing ring follows controls across dialogs or layouts, and nothing sends on its own:
 * an opener only opens; the person still confirms inside. The intent is consumed once —
 * refresh or Back never re-opens it.
 */
export default function SpotlightCue() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get('cue');
    if (fromUrl) {
      rememberCue(location.pathname, fromUrl);
      const params = new URLSearchParams(location.search);
      params.delete('cue');
      const search = setupIntentSearch(location.pathname, fromUrl, params.toString());
      navigate(`${location.pathname}${search ? `?${search}` : ''}${location.hash}`, { replace: true });
      return () => {};
    }
    const id = pendingCueFor(location.pathname);
    if (!id) return;

    const search = setupIntentSearch(location.pathname, id, location.search);
    if (search !== new URLSearchParams(location.search).toString()) {
      navigate(`${location.pathname}${search ? `?${search}` : ''}${location.hash}`, { replace: true });
      return;
    }

    let done = false;
    const finish = () => {
      done = true;
      observer.disconnect();
      clearCue(location.pathname, id);
    };
    const attach = () => {
      if (done) return;
      const el = Array.from(document.querySelectorAll<HTMLElement>('[data-cue]'))
        .find((node) => node.dataset.cue === id && !node.matches(':disabled') && node.getClientRects().length > 0);
      if (!el) return;
      finish();
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView?.({ behavior: reduce ? 'instant' : 'smooth', block: 'center' });
      if (isOpenerCue(id)) {
        // Radix menu triggers open on pointerdown; plain buttons open on click.
        if (el.getAttribute('aria-haspopup') === 'menu') {
          el.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true, button: 0, pointerType: 'mouse' }));
        } else {
          el.click();
        }
      } else {
        el.focus?.({ preventScroll: true });
      }
    };
    // Lazy pages and dialogs have no arrival deadline; the first visible match wins, once.
    const observer = new MutationObserver(attach);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'data-state'] });
    attach();
    return () => observer.disconnect();
  }, [location.key, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    const path = location.pathname;
    return () => clearCue(path);
  }, [location.pathname]);

  return null;
}
